/**
 * Lesson materials UI primitives — reused on the student dashboard, the
 * teacher dashboard, and inside the live classroom.
 *
 * MaterialCard       — renders a single material with open/delete affordances.
 * MaterialUploader   — drag-and-drop / file-picker that calls `onUpload(file)`.
 *
 * No data fetching here — the parent owns state and passes callbacks. That
 * keeps these usable everywhere a list of materials needs to show up.
 */
import React, { useRef, useState } from "react";
import { toast } from "sonner";

const ACCEPT_MIMES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "audio/mpeg",
  "audio/mp3",
  "video/mp4",
];
const ACCEPT_ATTR = ".pdf,.jpg,.jpeg,.png,.mp3,.mp4";
const MAX_FILE_BYTES = 50 * 1024 * 1024;

export function MaterialUploader({ onUpload, busy, compact = false, "data-testid": testId = "material-uploader" }) {
  const inputRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [error, setError] = useState("");

  function validate(file) {
    if (!file) return "No file selected.";
    if (!ACCEPT_MIMES.includes(file.type)) {
      return "Only PDF, JPG, PNG, MP3, MP4 are accepted.";
    }
    if (file.size > MAX_FILE_BYTES) {
      return `File is too large. Max 50 MB.`;
    }
    return "";
  }

  async function handleFile(file) {
    const err = validate(file);
    if (err) { setError(err); return; }
    setError("");
    try {
      await onUpload(file);
    } catch (e) {
      setError(e?.message || "Upload failed.");
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setHover(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  return (
    <div data-testid={testId}>
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={onDrop}
        className={[
          "w-full text-left rounded-2xl border-2 border-dashed transition-colors",
          compact ? "px-4 py-3" : "px-6 py-7",
          hover ? "border-[#1C1C1C]/40 bg-[#1C1C1C]/5" : "border-[#1C1C1C]/15 hover:border-[#1C1C1C]/30 bg-white",
          busy ? "opacity-60 cursor-wait" : "cursor-pointer",
        ].join(" ")}
        data-testid={`${testId}-button`}
      >
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1C1C1C]/5 text-[#1C1C1C]/60 text-lg">
            ↑
          </span>
          <div>
            <div className="text-sm text-[#1C1C1C]">
              {busy ? "Uploading…" : compact ? "Add a file" : "Drop a file or click to upload"}
            </div>
            {!compact && (
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/45 mt-1">
                PDF · JPG · PNG · MP3 · MP4 · 50 MB max
              </div>
            )}
          </div>
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";  // allow re-uploading the same file later
          if (file) handleFile(file);
        }}
      />
      {error && (
        <div data-testid={`${testId}-error`} className="mt-2 text-xs text-[#B83A3A]">
          {error}
        </div>
      )}
    </div>
  );
}

const MIME_ICON = {
  "application/pdf": "PDF",
  "image/jpeg": "IMG",
  "image/png": "IMG",
  "audio/mpeg": "MP3",
  "audio/mp3": "MP3",
  "video/mp4": "MP4",
};

function fmtSize(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDateShort(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString([], {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

export function MaterialCard({
  material,
  fileUrl,
  onDelete,
  showLesson = false,
  "data-testid": testId,
}) {
  const [deleting, setDeleting] = useState(false);
  const tag = MIME_ICON[material.mime] || "FILE";
  const uploaderLabel = material.uploaded_by === "teacher"
    ? `Shared by ${material.uploader_name || "your teacher"}`
    : "Your upload";

  async function handleDelete() {
    if (!onDelete) return;
    if (!window.confirm(`Delete "${material.filename}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await onDelete(material);
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.message || "Could not delete.");
      setDeleting(false);
    }
  }

  return (
    <article
      data-testid={testId || `material-card-${material.id}`}
      className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-4 md:p-5 flex flex-wrap items-center gap-4"
    >
      <div className="w-12 h-12 rounded-xl bg-[#1C1C1C]/5 text-[#1C1C1C]/70 flex items-center justify-center text-[10px] tracking-[0.18em] font-medium shrink-0">
        {tag}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-[#1C1C1C] truncate" title={material.filename}>
          {material.filename}
        </div>
        <div className="text-[11px] tracking-[0.06em] text-[#1C1C1C]/55 mt-0.5">
          {uploaderLabel} · {fmtSize(material.size)}
          {showLesson && material.lesson_title && (
            <> · <span className="text-[#1C1C1C]/45">{material.lesson_title}</span></>
          )}
        </div>
        {showLesson && material.lesson_start && (
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/40 mt-1">
            {fmtDateShort(material.lesson_start)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`material-open-${material.id}`}
          className="px-3 py-2 bg-[#1C1C1C] text-[#FAF9F6] rounded-lg text-[10px] tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
        >
          Open →
        </a>
        {onDelete && (
          <button
            type="button"
            disabled={deleting}
            onClick={handleDelete}
            data-testid={`material-delete-${material.id}`}
            className="px-3 py-2 text-[#1C1C1C]/55 text-[10px] tracking-[0.18em] uppercase hover:text-[#B83A3A] disabled:opacity-40 transition-colors"
          >
            {deleting ? "…" : "Delete"}
          </button>
        )}
      </div>
    </article>
  );
}
